import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Heart, Send, Search, Star, Eye, EyeOff, ArrowRight, Link2, Copy, Clock, CheckCircle2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () => localStorage.getItem('token');
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// ═════ TABS ═════
const TABS = [
  { id: 'referrals', label: 'Referrals', icon: Users },
  { id: 'appreciations', label: 'Appreciations', icon: Heart },
];

// ═════ USER SEARCH ═════
const UserSearch = ({ onSelect, placeholder }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`, authHeaders());
        setResults(res.data);
      } catch { setResults([]); }
      setSearching(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
        <Input data-testid="user-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
          className="pl-10 bg-[#1E1E1E] border-[#27272A] text-white" placeholder={placeholder || "Search users..."} />
      </div>
      {results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 rounded-xl bg-[#1E1E1E] border border-[#27272A] overflow-hidden shadow-xl">
          {results.map(u => (
            <button key={u.username} data-testid={`user-result-${u.username}`}
              onClick={() => { onSelect(u); setQuery(''); setResults([]); }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left">
              <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center overflow-hidden">
                {u.avatar_url ? <img src={resolveMediaUrl(u.avatar_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-violet-400 text-xs font-bold">{(u.username || '?')[0].toUpperCase()}</span>}
              </div>
              <div>
                <p className="text-white text-sm font-medium">{u.display_name || u.owner_name || u.username}</p>
                <p className="text-[#71717A] text-xs">@{u.username}{u.business_name ? ` · ${u.business_name}` : ''}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {searching && <p className="text-xs text-[#71717A] mt-1">Searching...</p>}
    </div>
  );
};

// ═════ REFERRAL FORM ═════
const ReferralForm = ({ onSuccess }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [form, setForm] = useState({ client_name: '', client_contact: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!selectedUser || !form.client_name) return;
    setSending(true);
    try {
      await axios.post(`${API}/referrals`, { to_username: selectedUser.username, ...form }, authHeaders());
      toast.success(`Referral sent to @${selectedUser.username}!`);
      setSelectedUser(null);
      setForm({ client_name: '', client_contact: '', message: '' });
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send referral');
    }
    setSending(false);
  };

  return (
    <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
      <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Send className="w-4 h-4 text-violet-400" /> Send a Referral</h3>
      <div className="space-y-4">
        <div>
          <Label className="text-[#EDEDED]">Refer business to</Label>
          {selectedUser ? (
            <div className="mt-2 flex items-center justify-between p-3 rounded-xl bg-violet-500/10 border border-violet-500/30">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <span className="text-violet-400 text-xs font-bold">{(selectedUser.username || '?')[0].toUpperCase()}</span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{selectedUser.display_name || selectedUser.username}</p>
                  <p className="text-[#71717A] text-xs">@{selectedUser.username}</p>
                </div>
              </div>
              <button onClick={() => setSelectedUser(null)} className="text-[#71717A] hover:text-white text-xs">Change</button>
            </div>
          ) : (
            <div className="mt-2"><UserSearch onSelect={setSelectedUser} placeholder="Search by username..." /></div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[#EDEDED]">Client Name *</Label>
            <Input data-testid="referral-client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="John Smith" />
          </div>
          <div>
            <Label className="text-[#EDEDED]">Client Contact</Label>
            <Input data-testid="referral-client-contact" value={form.client_contact} onChange={(e) => setForm({ ...form, client_contact: e.target.value })}
              className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white" placeholder="email or phone" />
          </div>
        </div>
        <div>
          <Label className="text-[#EDEDED]">Message</Label>
          <Textarea data-testid="referral-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white resize-none" placeholder="I'm referring this client because..." rows={2} />
        </div>
        <Button data-testid="send-referral-btn" onClick={handleSend} disabled={!selectedUser || !form.client_name || sending}
          className="bg-violet-500 hover:bg-violet-600 text-white w-full sm:w-auto">
          <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send Referral'}
        </Button>
      </div>
    </div>
  );
};

// ═════ REFERRAL LINK GENERATOR ═════
const ReferralLinkGen = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [refLink, setRefLink] = useState(null);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!selectedUser) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/referrals/link`, { to_username: selectedUser.username }, authHeaders());
      setRefLink(`${window.location.origin}/ref/${res.data.ref_code}`);
      toast.success('Referral link created!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setGenerating(false);
  };

  return (
    <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
      <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Link2 className="w-4 h-4 text-emerald-400" /> Trackable Referral Link</h3>
      <p className="text-sm text-[#71717A] mb-4">Generate a unique link to share someone's profile. Clicks are tracked.</p>
      <div className="space-y-4">
        {selectedUser ? (
          <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
            <p className="text-white text-sm">Generating link for <span className="font-semibold">@{selectedUser.username}</span></p>
            <button onClick={() => { setSelectedUser(null); setRefLink(null); }} className="text-[#71717A] hover:text-white text-xs">Change</button>
          </div>
        ) : (
          <UserSearch onSelect={(u) => { setSelectedUser(u); setRefLink(null); }} placeholder="Search user to refer..." />
        )}
        {selectedUser && !refLink && (
          <Button data-testid="generate-ref-link-btn" onClick={generate} disabled={generating} className="bg-emerald-500 hover:bg-emerald-600 text-white">
            {generating ? 'Generating...' : 'Generate Link'}
          </Button>
        )}
        {refLink && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-[#1E1E1E] border border-[#27272A]">
            <input readOnly value={refLink} className="flex-1 bg-transparent text-emerald-400 text-sm outline-none" />
            <button data-testid="copy-ref-link-btn" onClick={() => { navigator.clipboard.writeText(refLink); toast.success('Copied!'); }}
              className="p-2 rounded-lg hover:bg-white/5"><Copy className="w-4 h-4 text-white" /></button>
          </div>
        )}
      </div>
    </div>
  );
};

// ═════ APPRECIATION FORM ═════
const AppreciationForm = ({ onSuccess }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(5);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!selectedUser || !message) return;
    setSending(true);
    try {
      await axios.post(`${API}/appreciations`, { to_username: selectedUser.username, message, rating }, authHeaders());
      toast.success(`Appreciation sent to @${selectedUser.username}!`);
      setSelectedUser(null);
      setMessage('');
      setRating(5);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setSending(false);
  };

  return (
    <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
      <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Heart className="w-4 h-4 text-rose-400" /> Give Appreciation</h3>
      <div className="space-y-4">
        <div>
          <Label className="text-[#EDEDED]">Appreciate</Label>
          {selectedUser ? (
            <div className="mt-2 flex items-center justify-between p-3 rounded-xl bg-rose-500/10 border border-rose-500/30">
              <p className="text-white text-sm font-medium">@{selectedUser.username}</p>
              <button onClick={() => setSelectedUser(null)} className="text-[#71717A] hover:text-white text-xs">Change</button>
            </div>
          ) : (
            <div className="mt-2"><UserSearch onSelect={setSelectedUser} placeholder="Search by username..." /></div>
          )}
        </div>
        <div>
          <Label className="text-[#EDEDED]">Rating</Label>
          <div className="flex gap-1 mt-2">
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} data-testid={`star-${s}`} onClick={() => setRating(s)}
                className="transition-transform hover:scale-110">
                <Star className={`w-7 h-7 ${s <= rating ? 'text-amber-400 fill-amber-400' : 'text-[#27272A]'}`} />
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[#EDEDED]">Message *</Label>
          <Textarea data-testid="appreciation-message" value={message} onChange={(e) => setMessage(e.target.value)}
            className="mt-2 bg-[#1E1E1E] border-[#27272A] text-white resize-none" placeholder="Great working with you! Your service was excellent..." rows={3} />
        </div>
        <Button data-testid="send-appreciation-btn" onClick={handleSend} disabled={!selectedUser || !message || sending}
          className="bg-rose-500 hover:bg-rose-600 text-white w-full sm:w-auto">
          <Heart className="w-4 h-4 mr-2" /> {sending ? 'Sending...' : 'Send Appreciation'}
        </Button>
      </div>
    </div>
  );
};

// ═════ STATUS BADGE ═════
const StatusBadge = ({ status }) => {
  const colors = {
    pending: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    contacted: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    converted: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${colors[status] || colors.pending}`}>
      {status}
    </span>
  );
};

// ═════ MAIN COMPONENT ═════
const NetworkHub = () => {
  const [activeTab, setActiveTab] = useState('referrals');
  const [referrals, setReferrals] = useState({ given: [], received: [], given_count: 0, received_count: 0 });
  const [appreciations, setAppreciations] = useState({ received: [], given: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [refRes, appRes] = await Promise.all([
        axios.get(`${API}/referrals`, authHeaders()),
        axios.get(`${API}/appreciations`, authHeaders()),
      ]);
      setReferrals(refRes.data);
      setAppreciations(appRes.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateRefStatus = async (id, status) => {
    try {
      await axios.put(`${API}/referrals/${id}/status`, { status }, authHeaders());
      toast.success('Status updated');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const toggleVisibility = async (id, isPublic) => {
    try {
      await axios.put(`${API}/appreciations/${id}/visibility`, { is_public: !isPublic }, authHeaders());
      toast.success(isPublic ? 'Hidden from profile' : 'Shown on profile');
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div data-testid="network-hub" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-white">Network Hub</h1>
        <p className="text-[#71717A] mt-1">Refer business, share contacts, and give appreciation</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl bg-[#121212] border border-[#27272A] p-4 text-center">
          <p className="text-2xl font-bold text-violet-400">{referrals.given_count}</p>
          <p className="text-xs text-[#71717A] mt-1">Referrals Given</p>
        </div>
        <div className="rounded-xl bg-[#121212] border border-[#27272A] p-4 text-center">
          <p className="text-2xl font-bold text-emerald-400">{referrals.received_count}</p>
          <p className="text-xs text-[#71717A] mt-1">Referrals Received</p>
        </div>
        <div className="rounded-xl bg-[#121212] border border-[#27272A] p-4 text-center">
          <p className="text-2xl font-bold text-rose-400">{appreciations.received?.length || 0}</p>
          <p className="text-xs text-[#71717A] mt-1">Appreciations</p>
        </div>
        <div className="rounded-xl bg-[#121212] border border-[#27272A] p-4 text-center">
          <p className="text-2xl font-bold text-amber-400">{appreciations.given?.length || 0}</p>
          <p className="text-xs text-[#71717A] mt-1">Given</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-2 border-b border-[#27272A] pb-0">
        {TABS.map(tab => (
          <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${activeTab === tab.id ? 'text-white border-violet-500' : 'text-[#71717A] border-transparent hover:text-white'}`}>
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'referrals' && (
          <motion.div key="referrals" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
            <ReferralForm onSuccess={fetchData} />
            <ReferralLinkGen />

            {/* Received Referrals */}
            {referrals.received.length > 0 && (
              <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> Referrals Received</h3>
                <div className="space-y-3">
                  {referrals.received.map(ref => (
                    <div key={ref.id} className="flex items-start justify-between p-4 rounded-xl bg-[#0a0a0a] border border-[#1a1a1a]">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white text-sm font-medium">{ref.client_name}</p>
                          <StatusBadge status={ref.status} />
                        </div>
                        <p className="text-xs text-[#71717A] mt-1">From @{ref.from_username} · {new Date(ref.created_at).toLocaleDateString()}</p>
                        {ref.message && <p className="text-sm text-[#A1A1AA] mt-2">{ref.message}</p>}
                        {ref.client_contact && <p className="text-xs text-violet-400 mt-1">{ref.client_contact}</p>}
                      </div>
                      <div className="flex gap-1 ml-3">
                        {ref.status === 'pending' && (
                          <button data-testid={`ref-contacted-${ref.id}`} onClick={() => updateRefStatus(ref.id, 'contacted')}
                            className="px-2 py-1 rounded-lg text-xs bg-blue-500/10 text-blue-400 hover:bg-blue-500/20">Contacted</button>
                        )}
                        {(ref.status === 'pending' || ref.status === 'contacted') && (
                          <button data-testid={`ref-converted-${ref.id}`} onClick={() => updateRefStatus(ref.id, 'converted')}
                            className="px-2 py-1 rounded-lg text-xs bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20">Converted</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Given Referrals */}
            {referrals.given.length > 0 && (
              <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><ArrowRight className="w-4 h-4 text-violet-400" /> Referrals Given</h3>
                <div className="space-y-3">
                  {referrals.given.map(ref => (
                    <div key={ref.id} className="p-4 rounded-xl bg-[#0a0a0a] border border-[#1a1a1a]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white text-sm font-medium">{ref.client_name}</p>
                        <ArrowRight className="w-3 h-3 text-[#71717A]" />
                        <p className="text-violet-400 text-sm">@{ref.to_username}</p>
                        <StatusBadge status={ref.status} />
                      </div>
                      <p className="text-xs text-[#71717A] mt-1">{new Date(ref.created_at).toLocaleDateString()}</p>
                      {ref.message && <p className="text-sm text-[#A1A1AA] mt-2">{ref.message}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'appreciations' && (
          <motion.div key="appreciations" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
            <AppreciationForm onSuccess={fetchData} />

            {/* Received */}
            {appreciations.received?.length > 0 && (
              <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
                <h3 className="text-white font-semibold mb-4">Received Appreciations</h3>
                <div className="space-y-3">
                  {appreciations.received.map(a => (
                    <div key={a.id} className="flex items-start justify-between p-4 rounded-xl bg-[#0a0a0a] border border-[#1a1a1a]">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-white text-sm font-medium">@{a.from_username}</p>
                          <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= a.rating ? 'text-amber-400 fill-amber-400' : 'text-[#27272A]'}`} />)}</div>
                        </div>
                        <p className="text-sm text-[#A1A1AA] mt-1">{a.message}</p>
                        <p className="text-xs text-[#71717A] mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
                      </div>
                      <button data-testid={`toggle-vis-${a.id}`} onClick={() => toggleVisibility(a.id, a.is_public)}
                        className={`p-2 rounded-lg transition-colors ${a.is_public ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-[#71717A] hover:bg-white/5'}`}
                        title={a.is_public ? 'Visible on profile' : 'Hidden from profile'}>
                        {a.is_public ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Given */}
            {appreciations.given?.length > 0 && (
              <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6">
                <h3 className="text-white font-semibold mb-4">Given Appreciations</h3>
                <div className="space-y-3">
                  {appreciations.given.map(a => (
                    <div key={a.id} className="p-4 rounded-xl bg-[#0a0a0a] border border-[#1a1a1a]">
                      <div className="flex items-center gap-2">
                        <p className="text-violet-400 text-sm">@{a.to_username}</p>
                        <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= a.rating ? 'text-amber-400 fill-amber-400' : 'text-[#27272A]'}`} />)}</div>
                      </div>
                      <p className="text-sm text-[#A1A1AA] mt-1">{a.message}</p>
                      <p className="text-xs text-[#71717A] mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NetworkHub;
